<template>
  <div style="border-radius: 0.625rem; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.08); border: 1px solid #E5E7EB;">

    <!-- ═══ PARENT CAMPAIGN ROW ═══ -->
    <div style="display: flex; align-items: center; padding: 1rem 1.125rem; gap: 1rem; background: white; border-left: 4px solid #EC4899;">

      <!-- Icon -->
      <div :style="{ flexShrink: 0, width: '2.5rem', height: '2.5rem', borderRadius: '0.625rem', background: 'linear-gradient(135deg, #EC4899, #DB2777)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(236, 72, 153, 0.3)' }">
        <svg v-if="hasChildren" xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem; color: white;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem; color: white;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      </div>

      <!-- Info -->
      <div style="flex: 1; min-width: 0;">
        <div style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
          <a :href="'/campaigns/' + campaign.id" target="_blank" style="font-size: 1rem; font-weight: 700; color: #111827; text-decoration: none;" @mouseover="$event.target.style.color='#EC4899'" @mouseout="$event.target.style.color='#111827'">
            {{ campaign.name }}
          </a>
          <span v-if="campaign.isPledgeDrive" style="font-size: 0.6875rem; padding: 0.1875rem 0.5rem; background: #EFF6FF; color: #1D4ED8; border-radius: 9999px; font-weight: 600; border: 1px solid #BFDBFE;">
            Pledge Drive
          </span>
          <span v-if="hasChildren" style="font-size: 0.6875rem; padding: 0.1875rem 0.625rem; background: linear-gradient(135deg, #FDF2F8, #FCE7F3); color: #BE185D; border-radius: 9999px; font-weight: 600; border: 1px solid #FBCFE8;">
            {{ campaign.children_node_count }} supporter{{ campaign.children_node_count !== 1 ? 's' : '' }}
          </span>
        </div>

        <!-- Progress bar (standard) -->
        <div v-if="!campaign.isPledgeDrive" style="margin-top: 0.5rem;">
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <div style="flex: 1; height: 0.375rem; background: #F3F4F6; border-radius: 9999px; overflow: hidden;">
              <div :style="{ height: '100%', background: 'linear-gradient(90deg, #10B981, #059669)', borderRadius: '9999px', width: Math.min(campaign.funded || 0, 100) + '%', transition: 'width 0.5s ease' }"></div>
            </div>
            <span style="font-size: 0.6875rem; color: #9CA3AF; font-weight: 500; white-space: nowrap;">{{ campaign.funded || 0 }}%</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-top: 0.25rem;">
            <span style="font-size: 0.75rem; font-weight: 600; color: #374151;">${{ campaign.total_raised_with_children_campaigns }} Raised</span>
            <span style="font-size: 0.75rem; color: #9CA3AF;">${{ campaign.goal }} Goal</span>
          </div>
        </div>

        <!-- Progress bar (pledge drive) -->
        <div v-else style="margin-top: 0.5rem;">
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <div style="flex: 1; height: 0.375rem; background: #F3F4F6; border-radius: 9999px; overflow: hidden;">
              <div :style="{ height: '100%', background: 'linear-gradient(90deg, #10B981, #059669)', borderRadius: '9999px', width: Math.min(campaign.pledge_drive?.achieved || 0, 100) + '%' }"></div>
            </div>
            <span style="font-size: 0.6875rem; color: #9CA3AF; font-weight: 500; white-space: nowrap;">{{ campaign.pledge_drive?.achieved || 0 }}%</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-top: 0.25rem;">
            <span style="font-size: 0.75rem; font-weight: 600; color: #374151;">Achieved</span>
            <span style="font-size: 0.75rem; color: #9CA3AF;">Goal: {{ campaign.pledge_drive?.goal }} {{ campaign.pledge_drive?.activity_type }}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-top: 0.125rem;">
            <span style="font-size: 0.6875rem; color: #6B7280;">${{ campaign.pledge_drive?.pledge_total_donation_amount }} Raised</span>
            <span style="font-size: 0.6875rem; color: #6B7280;">Est. ${{ campaign.pledge_drive?.formattedTotalAmountDue }}</span>
          </div>
        </div>

        <!-- Meta row -->
        <div style="display: flex; gap: 1rem; margin-top: 0.375rem; flex-wrap: wrap;">
          <a v-if="hasChildren" :href="'/sub-campaigns/' + campaign.id" style="font-size: 0.75rem; font-weight: 600; color: #EC4899; text-decoration: none;" @mouseover="$event.target.style.color='#BE185D'" @mouseout="$event.target.style.color='#EC4899'">
            {{ campaign.children_node_count }} Supporters
          </a>
          <span style="font-size: 0.75rem; color: #6B7280;">
            {{ campaign.donation_count_directly_including_children || 0 }} Donors
          </span>
          <a v-if="campaign.isPledgeDrive && campaign.pledge_drive" :href="'/campaign/' + campaign.id + '/pledge-drives/' + campaign.pledge_drive.id + '/pledges'" style="font-size: 0.75rem; color: #EC4899; text-decoration: none; font-weight: 500;">
            {{ campaign.pledge_drive?.pledge_count || 0 }} Pledges
          </a>
        </div>
      </div>

      <!-- Action buttons -->
      <div style="display: flex; flex-direction: column; gap: 0.25rem; flex-shrink: 0;">
        <a :href="'/edit-campaign/' + campaign.id" style="padding: 0.375rem; color: #6B7280; text-decoration: none; border-radius: 0.25rem;" @mouseover="$event.target.closest('a').style.color='#EC4899'; $event.target.closest('a').style.background='#FDF2F8'" @mouseout="$event.target.closest('a').style.color='#6B7280'; $event.target.closest('a').style.background='transparent'" title="Edit">
          <svg xmlns="http://www.w3.org/2000/svg" style="width: 1rem; height: 1rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </a>
        <a :href="'/campaigns/' + campaign.id" target="_blank" style="padding: 0.375rem; color: #6B7280; text-decoration: none; border-radius: 0.25rem;" @mouseover="$event.target.closest('a').style.color='#EC4899'; $event.target.closest('a').style.background='#FDF2F8'" @mouseout="$event.target.closest('a').style.color='#6B7280'; $event.target.closest('a').style.background='transparent'" title="View Page">
          <svg xmlns="http://www.w3.org/2000/svg" style="width: 1rem; height: 1rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
        <a v-if="hasChildren" :href="'/sub-campaigns/' + campaign.id" style="padding: 0.375rem; color: #6B7280; text-decoration: none; border-radius: 0.25rem;" @mouseover="$event.target.closest('a').style.color='#EC4899'; $event.target.closest('a').style.background='#FDF2F8'" @mouseout="$event.target.closest('a').style.color='#6B7280'; $event.target.closest('a').style.background='transparent'" title="Manage Supporters">
          <svg xmlns="http://www.w3.org/2000/svg" style="width: 1rem; height: 1rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </a>
        <a v-if="campaign.impact_score" :href="'/admin/reports/impact-leaderboard/' + campaign.id" style="padding: 0.375rem; color: #6B7280; text-decoration: none; border-radius: 0.25rem;" @mouseover="$event.target.closest('a').style.color='#EC4899'; $event.target.closest('a').style.background='#FDF2F8'" @mouseout="$event.target.closest('a').style.color='#6B7280'; $event.target.closest('a').style.background='transparent'" title="Leaderboard">
          <svg xmlns="http://www.w3.org/2000/svg" style="width: 1rem; height: 1rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </a>
      </div>
    </div>

    <!-- Pledge Drive Manager button -->
    <div v-if="campaign.isPledgeDrive && campaign.pledge_drive" style="padding: 0.5rem 1.125rem; background: white;">
      <a :href="'/campaign/' + campaign.id + '/pledge-drives/' + campaign.pledge_drive.id + '/pledges'" style="display: block; padding: 0.625rem; text-align: center; background: #EC4899; color: white; font-weight: 700; font-size: 0.8125rem; text-transform: uppercase; text-decoration: none; border-radius: 0.375rem;" @mouseover="$event.target.style.background='#DB2777'" @mouseout="$event.target.style.background='#EC4899'">
        Pledge Drive Manager
      </a>
    </div>

    <!-- ─── CHILDREN (nested hierarchy) ─── -->
    <div v-if="children.length > 0" style="background: #F8FAFC; border-top: 1px solid #E5E7EB;">
      <!-- Section label -->
      <div style="padding: 0.5rem 1.125rem 0.25rem 3.75rem; font-size: 0.625rem; text-transform: uppercase; letter-spacing: 0.08em; color: #9CA3AF; font-weight: 600;">
        Supporting Campaigns
      </div>

      <div v-for="(child, ci) in children" :key="child.id">
        <!-- Child row -->
        <div
          style="display: flex; align-items: center; padding: 0.625rem 1.125rem 0.625rem 3.75rem; gap: 0.625rem;"
          :style="{ borderBottom: (ci < children.length - 1 || (child.children && child.children.length > 0)) ? '1px solid #EFF1F3' : 'none' }"
          @mouseover="$event.currentTarget.style.background='#EFF6FF'"
          @mouseout="$event.currentTarget.style.background='transparent'"
        >
          <!-- Connector line -->
          <div style="flex-shrink: 0; width: 1rem; display: flex; align-items: center; justify-content: center; color: #D1D5DB;">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M0 0 L0 6 L12 6" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/>
            </svg>
          </div>

          <!-- Icon -->
          <div :style="{ flexShrink: 0, width: '1.75rem', height: '1.75rem', borderRadius: '0.5rem', background: (child.children && child.children.length > 0) ? 'linear-gradient(135deg, #FDF2F8, #FCE7F3)' : 'white', border: (child.children && child.children.length > 0) ? '1.5px solid #FBCFE8' : '1.5px solid #D1D5DB', display: 'flex', alignItems: 'center', justifyContent: 'center' }">
            <svg v-if="child.children && child.children.length > 0" xmlns="http://www.w3.org/2000/svg" style="width: 0.8125rem; height: 0.8125rem; color: #EC4899;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" style="width: 0.8125rem; height: 0.8125rem; color: #9CA3AF;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>

          <!-- Name + location -->
          <div style="flex: 1; min-width: 0;">
            <div style="display: flex; align-items: center; gap: 0.375rem; flex-wrap: wrap;">
              <a :href="'/campaigns/' + child.id" target="_blank" style="font-size: 0.875rem; font-weight: 600; color: #374151; text-decoration: none;" @mouseover="$event.target.style.color='#EC4899'" @mouseout="$event.target.style.color='#374151'">
                {{ truncate(child.name, 45) }}
              </a>
              <span v-if="child.children && child.children.length > 0" style="font-size: 0.625rem; padding: 0.125rem 0.375rem; background: linear-gradient(135deg, #FDF2F8, #FCE7F3); color: #BE185D; border-radius: 9999px; font-weight: 600; border: 1px solid #FBCFE8;">
                {{ child.children.length }} sub-team{{ child.children.length > 1 ? 's' : '' }}
              </span>
            </div>
            <div v-if="child.city || child.state || child.business_name" style="font-size: 0.75rem; color: #9CA3AF; margin-top: 0.0625rem;">
              {{ child.business_name ? child.business_name + ' · ' : '' }}{{ child.city }}{{ child.city && child.state ? ', ' : '' }}{{ child.state }}
            </div>
          </div>

          <!-- Stats -->
          <div style="text-align: right; flex-shrink: 0; min-width: 80px;">
            <div style="font-size: 0.875rem; font-weight: 600; color: #374151;">${{ formatCurrency(child.total_raised_with_children_campaigns) }}</div>
            <div style="height: 0.25rem; width: 60px; background: #F3F4F6; border-radius: 9999px; overflow: hidden; margin-top: 0.25rem; margin-left: auto;">
              <div :style="{ height: '100%', background: '#EC4899', borderRadius: '9999px', width: Math.min(child.funded || 0, 100) + '%' }"></div>
            </div>
          </div>

          <!-- Quick actions -->
          <div style="display: flex; gap: 0.125rem; flex-shrink: 0;">
            <a :href="'/edit-campaign/' + child.id" style="padding: 0.25rem; color: #C4C9D1; text-decoration: none; border-radius: 0.25rem;" @mouseover="$event.target.closest('a').style.color='#EC4899'" @mouseout="$event.target.closest('a').style.color='#C4C9D1'" title="Edit">
              <svg xmlns="http://www.w3.org/2000/svg" style="width: 0.8125rem; height: 0.8125rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </a>
            <a :href="'/campaigns/' + child.id" target="_blank" style="padding: 0.25rem; color: #C4C9D1; text-decoration: none; border-radius: 0.25rem;" @mouseover="$event.target.closest('a').style.color='#EC4899'" @mouseout="$event.target.closest('a').style.color='#C4C9D1'" title="View">
              <svg xmlns="http://www.w3.org/2000/svg" style="width: 0.8125rem; height: 0.8125rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>

        <!-- ─── GRANDCHILDREN (third level) ─── -->
        <div v-if="child.children && child.children.length > 0">
          <div
            v-for="(grandchild, gi) in child.children"
            :key="grandchild.id"
            style="display: flex; align-items: center; padding: 0.4375rem 1.125rem 0.4375rem 6.5rem; gap: 0.5rem;"
            :style="{ borderBottom: (gi < child.children.length - 1) ? '1px solid #F3F4F6' : (ci < children.length - 1 ? '1px solid #EFF1F3' : 'none') }"
            @mouseover="$event.currentTarget.style.background='#F0F9FF'"
            @mouseout="$event.currentTarget.style.background='transparent'"
          >
            <!-- Connector -->
            <div style="flex-shrink: 0; width: 0.75rem; display: flex; align-items: center; justify-content: center; color: #E5E7EB;">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M0 0 L0 5 L10 5" stroke="currentColor" stroke-width="1.25" fill="none" stroke-linecap="round"/>
              </svg>
            </div>

            <!-- Tiny icon -->
            <div style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; border-radius: 0.25rem; background: white; border: 1px solid #E5E7EB; display: flex; align-items: center; justify-content: center;">
              <svg xmlns="http://www.w3.org/2000/svg" style="width: 0.625rem; height: 0.625rem; color: #C4C9D1;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>

            <!-- Name -->
            <div style="flex: 1; min-width: 0;">
              <a :href="'/campaigns/' + grandchild.id" target="_blank" style="font-size: 0.75rem; font-weight: 500; color: #6B7280; text-decoration: none;" @mouseover="$event.target.style.color='#EC4899'" @mouseout="$event.target.style.color='#6B7280'">
                {{ truncate(grandchild.name, 35) }}
              </a>
              <span v-if="grandchild.city || grandchild.state" style="font-size: 0.6875rem; color: #C4C9D1; margin-left: 0.25rem;">
                {{ grandchild.city }}{{ grandchild.city && grandchild.state ? ', ' : '' }}{{ grandchild.state }}
              </span>
            </div>

            <!-- Amount -->
            <div style="font-size: 0.75rem; font-weight: 500; color: #9CA3AF; flex-shrink: 0;">
              ${{ formatCurrency(grandchild.total_raised_with_children_campaigns) }}
            </div>
          </div>
        </div>
      </div>

      <!-- View all link -->
      <div v-if="campaign.children_node_count > children.length" style="padding: 0.625rem 1.125rem; text-align: center; border-top: 1px solid #EFF1F3;">
        <a :href="'/sub-campaigns/' + campaign.id" style="font-size: 0.8125rem; color: #EC4899; text-decoration: none; font-weight: 500;" @mouseover="$event.target.style.color='#BE185D'" @mouseout="$event.target.style.color='#EC4899'">
          View all {{ campaign.children_node_count }} supporting campaigns →
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    campaign: { type: Object, required: true },
    children: { type: Array, default: () => [] },
  },
  computed: {
    hasChildren() {
      return (this.campaign.children_node_count || 0) > 0;
    },
  },
  methods: {
    formatCurrency(dollars) {
      if (!dollars) return '0';
      return Number(dollars).toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
    truncate(str, len) {
      if (!str) return '';
      return str.length > len ? str.substring(0, len) + '…' : str;
    },
  },
}
</script>
